import { Avatar, Skeleton, Table, formatDate, type Column } from "@helmr/ui";
import { Link, useNavigate } from "react-router-dom";
import type { Lead, LeadFilters } from "../../api/leads";
import { FailureBody } from "../home/states";
import { flatten, useLeadPages, useStages, useStaffNames, type Scope } from "./hooks";
import { leads as t } from "./strings";
import { LeadsEmpty, Owner, SourceChip, StageBadge, leadName } from "./ui";
import { localDateOf } from "@helmr/contracts";
import s from "./leads.module.css";

export function LeadListSkeleton() {
  return (
    <div className={s.skRows} aria-busy="true" data-testid="leads-skeleton">
      {Array.from({ length: 6 }, (_, i) => <div key={i} style={{ display: "grid", gridTemplateColumns: "30px 1fr", gap: 10 }}><Skeleton width={30} height={30} /><div style={{ display: "grid", gap: 6 }}><Skeleton width="45%" height={13} /><Skeleton width="70%" height={12} /></div></div>)}
    </div>
  );
}

export function LeadList({ sc, filters, stage, onStage, hasFilters }: { sc: Scope; filters: LeadFilters; stage: string; onStage: (id: string) => void; hasFilters: boolean }) {
  const pages = useLeadPages(sc, filters);
  const stages = useStages(sc);
  const staff = useStaffNames();
  const nav = useNavigate();
  const leads = flatten(pages.data?.pages);
  const stageOf = (id: string) => stages.data?.find((x) => x.id === id);
  const added = (l: Lead) => formatDate(localDateOf(Date.parse(l.createdAt), sc.ctx.timeZone), sc.ctx.locale, "short");

  const cols: Column<Lead>[] = [
    { key: "name", header: t.cols.name, cell: (l) => (
      <div className={s.nameCell}>
        <Avatar id={l.id} name={leadName(l)} size="sm" />
        <Link to={`/leads/${l.id}`} onClick={(e) => e.stopPropagation()}><b title={leadName(l)}>{leadName(l)}</b><small>{l.phone}</small></Link>
      </div>
    ) },
    { key: "stage", header: t.cols.stage, cell: (l) => <StageBadge stage={stageOf(l.stageId)} /> },
    { key: "source", header: t.cols.source, cell: (l) => <SourceChip source={l.source} /> },
    { key: "owner", header: t.cols.owner, cell: (l) => <Owner id={l.ownerId} name={staff.nameOf(l.ownerId)} /> },
    { key: "added", header: t.cols.added, cell: (l) => <span className={s.muted}>{added(l)}</span> },
  ];

  return (
    <>
      <div className={s.filters} style={{ padding: "10px 16px" }}>
        <select value={stage} onChange={(e) => onStage(e.target.value)} aria-label={t.filters.stage}>
          <option value="">{t.filters.allStages}</option>
          {(stages.data ?? []).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
      </div>
      {pages.isPending ? <LeadListSkeleton /> : pages.error ? (
        <FailureBody error={pages.error} title={t.error} what="Leads" onRetry={() => void pages.refetch()} />
      ) : leads.length === 0 ? (
        <LeadsEmpty sc={sc} filtered={hasFilters} />
      ) : (
        <Table
          label={t.title}
          columns={cols}
          rows={leads}
          rowKey={(l) => l.id}
          onRowClick={(l) => nav(`/leads/${l.id}`)}
          hasMore={!!pages.hasNextPage}
          onLoadMore={() => void pages.fetchNextPage()}
          loadingMore={pages.isFetchingNextPage}
          summary={t.summary(leads.length, !!pages.hasNextPage)}
          minWidth={640}
        />
      )}
    </>
  );
}
